import { themeDark } from 'styles/theme';
import calculateNiceStepSize from './calculateNiceStepSize';

/**
 * Shared Chart.js options used across all bar chart components.
 */
export const BASE_CHART_OPTIONS = {
  legend: { display: false },
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    animateScale: true,
    animateRotate: true,
  },
} as const;

/**
 * Shared X-axis tick styling.
 */
export const BASE_X_TICK_STYLE = {
  fontColor: themeDark.color.textLight,
  fontSize: 10,
} as const;

/**
 * Builds a Y-axis configuration for total (stacked) or net mode.
 */
export function buildYAxisConfig(params: {
  isNetMode: boolean;
  yAxisLabel: string;
  prefix: string;
  formatter: (num: number) => string | number;
  netValues?: number[];
}): Record<string, unknown> {
  const { isNetMode, yAxisLabel, prefix, formatter, netValues } = params;

  const baseConfig: Record<string, unknown> = {
    stacked: true,
    scaleLabel: {
      display: true,
      labelString: isNetMode ? `Net ${yAxisLabel}` : yAxisLabel,
      fontColor: themeDark.color.textLight,
      fontSize: 11,
    },
    ticks: {
      beginAtZero: true,
      callback: function (value: number) {
        return `${prefix}${formatter(Math.abs(Number(value)))}`;
      },
      fontColor: themeDark.color.textLight,
      fontSize: 11,
    },
  };

  if (isNetMode && netValues) {
    const maxAbs = Math.max(...netValues.map((v) => Math.abs(v)), 1);
    const stepSize = calculateNiceStepSize(maxAbs);
    const roundedMax = Math.ceil(maxAbs / stepSize) * stepSize;

    return {
      ...baseConfig,
      stacked: false,
      ticks: {
        ...(baseConfig.ticks as Record<string, unknown>),
        min: -roundedMax,
        max: roundedMax,
        stepSize,
        callback: function (value: number) {
          const absValue = Math.abs(Number(value));
          const sign = Number(value) < 0 ? '-' : '';
          return `${sign}${prefix}${formatter(absValue)}`;
        },
      },
    };
  }

  return baseConfig;
}

/**
 * Builds datasets for total (stacked) mode with calls/puts split.
 */
export function buildTotalDatasets<T>(
  data: T[],
  getters: {
    unusualCalls: (t: T) => number | null;
    normalCalls: (t: T) => number | null;
    unusualPuts: (t: T) => number | null;
    normalPuts: (t: T) => number | null;
  },
) {
  return [
    {
      type: 'bar' as const,
      label: 'Unusual Calls',
      data: data.map(getters.unusualCalls),
      backgroundColor: themeDark.color.graphUnusualCall,
      stack: 'calls',
    },
    {
      type: 'bar' as const,
      label: 'Normal Calls',
      data: data.map(getters.normalCalls),
      backgroundColor: themeDark.color.graphCall,
      stack: 'calls',
    },
    {
      type: 'bar' as const,
      label: 'Unusual Puts',
      data: data.map(getters.unusualPuts),
      backgroundColor: themeDark.color.graphUnusualPut,
      stack: 'puts',
    },
    {
      type: 'bar' as const,
      label: 'Normal Puts',
      data: data.map(getters.normalPuts),
      backgroundColor: themeDark.color.graphPut,
      stack: 'puts',
    },
  ];
}

/**
 * Builds a single dataset for net mode.
 */
export function buildNetDataset<T>(
  data: T[],
  getNet: (t: T) => number,
  shortLabel: string,
) {
  return [
    {
      type: 'bar' as const,
      label: `Net ${shortLabel}`,
      data: data.map(getNet),
      backgroundColor: data.map((t) =>
        getNet(t) >= 0 ? themeDark.color.graphCall : themeDark.color.graphPut,
      ),
    },
  ];
}

/**
 * Returns abbreviated label for dataset names.
 */
export function getShortLabel(label: string): string {
  if (label === 'Volume') return 'Vol';
  if (label === 'Premium') return 'Prem';
  return label;
}

/**
 * Gets the canvas chart context from a Chart.js tooltip `this` context.
 * Replaces @ts-ignore usage for `this._chart`.
 */
export function getChartFromTooltipContext(ctx: unknown): {
  canvas: HTMLCanvasElement;
  data: { labels: string[] };
} {
  return (ctx as { _chart: { canvas: HTMLCanvasElement; data: { labels: string[] } } })._chart;
}
